import { doc, getDoc, setDoc, deleteDoc, Timestamp, type DocumentReference } from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import type { CheckinMethod } from '../shared/types/checkin.js'

function isPermissionDenied(error: unknown): boolean {
  return (error as { code?: unknown } | null)?.code === 'permission-denied'
}

/** Whether the check-in document says checked in; false when it can't be read */
async function isCheckedIn(checkinRef: DocumentReference): Promise<boolean> {
  try {
    const snap = await getDoc(checkinRef)
    return snap.exists() && snap.data().checkedIn === true
  }
  catch {
    return false
  }
}

/**
 * Member info for check-in notification
 */
interface CheckinMemberInfo {
  name: string
  subkreweName?: string
  vipCount?: number
  parkingCount?: number
  childrenCount?: number
}

/**
 * Composable to manage check-ins
 * Handles creating, updating, and deleting check-in records
 */
export function useCheckin() {
  const db = useFirestore()
  const { paradeYear: defaultParadeYear } = useParadeYear()
  const currentUser = useCurrentUser()
  const { createCheckinConfirmedNotification } = useNotificationCreator()

  /**
   * Check in a member
   * @param memberId - UID of the member to check in
   * @param method - Check-in method (individual, captain_bulk, admin_manual)
   * @param captainInfo - Optional captain info for bulk check-ins
   * @param year - Parade year (defaults to next parade year)
   * @param memberInfo - Optional member info for sending notification
   * @returns Success boolean; also true when the rules refused the write
   *   because they were already checked in
   */
  async function checkinMember(
    memberId: string,
    method: CheckinMethod = 'individual',
    captainInfo?: { id: string, name: string },
    year?: number,
    memberInfo?: CheckinMemberInfo,
  ): Promise<boolean> {
    if (!currentUser.value?.uid) {
      console.error('Cannot check in: No authenticated user')
      return false
    }

    const paradeYear = year ?? defaultParadeYear.value
    const checkedInByName = currentUser.value.displayName || currentUser.value.email || 'Unknown'

    try {
      const checkinRef = doc(
        db,
        'members',
        memberId,
        'checkins',
        paradeYear.toString(),
      )

      const checkinData: {
        year: number
        checkedIn: boolean
        timestamp: Timestamp
        checkedInBy: string
        checkedInByName: string
        method: CheckinMethod
        captainId?: string
        captainName?: string
      } = {
        year: paradeYear,
        checkedIn: true,
        timestamp: Timestamp.now(),
        checkedInBy: currentUser.value.uid,
        checkedInByName,
        method,
      }

      // Add captain info if this is a bulk check-in
      if (method === 'captain_bulk' && captainInfo) {
        checkinData.captainId = captainInfo.id
        checkinData.captainName = captainInfo.name
      }

      try {
        await setDoc(checkinRef, checkinData)
      }
      catch (error) {
        // Only admins may overwrite a check-in, so the rules refuse a
        // volunteer's second write. If they are checked in, that's done, not a
        // failure (and they've had their notification)
        if (isPermissionDenied(error) && await isCheckedIn(checkinRef)) return true
        throw error
      }

      // Send notification if member info is provided
      if (memberInfo) {
        // For captain bulk check-ins, show the captain's name in the notification
        // (not the volunteer operating the check-in system)
        const displayName = (method === 'captain_bulk' && captainInfo)
          ? captainInfo.name
          : checkedInByName

        try {
          await createCheckinConfirmedNotification(
            memberId,
            memberInfo.name,
            paradeYear,
            displayName,
            memberInfo.subkreweName,
            {
              vipCount: memberInfo.vipCount,
              parkingCount: memberInfo.parkingCount,
              childrenCount: memberInfo.childrenCount,
            },
          )
        }
        catch (notificationError) {
          // Log but don't fail the check-in if notification fails
          console.error('Error sending check-in notification:', notificationError)
        }
      }

      return true
    }
    catch (error) {
      console.error('Error checking in member:', error)
      return false
    }
  }

  /**
   * Check in multiple members (bulk check-in)
   * @param memberIds - Array of member UIDs to check in
   * @param method - Check-in method
   * @param captainInfo - Optional captain info for captain bulk check-ins
   * @param year - Parade year
   * @returns Object with success count and failed member IDs
   */
  async function bulkCheckinMembers(
    memberIds: string[],
    method: CheckinMethod = 'captain_bulk',
    captainInfo?: { id: string, name: string },
    year?: number,
  ): Promise<{ successCount: number, failedIds: string[] }> {
    const results = await Promise.allSettled(
      memberIds.map(memberId =>
        checkinMember(memberId, method, captainInfo, year),
      ),
    )

    const failedIds: string[] = []
    let successCount = 0

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        successCount++
      }
      else {
        failedIds.push(memberIds[index]!)
      }
    })

    return { successCount, failedIds }
  }

  /**
   * Undo a check-in (admin only)
   * @param memberId - UID of the member
   * @param year - Parade year (defaults to next parade year)
   * @returns Success boolean
   */
  async function undoCheckin(
    memberId: string,
    year?: number,
  ): Promise<boolean> {
    if (!currentUser.value?.uid) {
      console.error('Cannot undo check-in: No authenticated user')
      return false
    }

    // Note: Firestore rules enforce that only admins can delete check-ins
    const paradeYear = year ?? defaultParadeYear.value

    try {
      const checkinRef = doc(
        db,
        'members',
        memberId,
        'checkins',
        paradeYear.toString(),
      )

      await deleteDoc(checkinRef)
      return true
    }
    catch (error) {
      console.error('Error undoing check-in:', error)
      return false
    }
  }

  return {
    checkinMember,
    bulkCheckinMembers,
    undoCheckin,
  }
}
